import { Box } from "@mui/material";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { AppPagination, ParagraphText } from "../../../../../Libraries";
import ItemRow from "./itemRow";
import { LanguageContext } from "../../../../../context/LanguageContext";
import styles from "./StockInOutList.module.css";
import NonIdealNew from "../../../../common/NonIdealNew";

const STOCK_IN_OUT_GRID_TEMPLATE = "minmax(0, 2fr) repeat(4, minmax(0, 1fr))";

const StockInOutList = memo(
  ({
    type,
    className,
    paramsMap,
    setParamsMap,
    data = [],
    totalStockCount,
    totalAllStocks,
    isLoading,
    isSingleStock,
    handleGetMoreData = () => { },
  }) => {
    const [fieldStates, setFieldStates] = useState({});
    const [I18n] = useContext(LanguageContext);
    const isInitial = useRef(false);

    useEffect(() => {
      if (isInitial.current) {
        setParamsMap((prevMap) => {
          const updatedMap = {};
          Object.keys(prevMap).forEach((rowIndex) => {
            const rowParams = prevMap[rowIndex];
            updatedMap[rowIndex] = {
              ...rowParams,
              type,
              amount:
                type === "in"
                  ? data[rowIndex].cost_price
                  : data[rowIndex].selling_price,
            };
          });
          return updatedMap;
        });
        setFieldStates((prevState) => {
          const updatedState = { ...prevState };

          data.forEach((item, rowIndex) => {
            const price = type === "in" ? item.cost_price : item.selling_price;

            updatedState[rowIndex] = {
              ...updatedState[rowIndex],
              price,
            };
          });

          return updatedState;
        });
      }
    }, [type]);
    useEffect(() => {
      isInitial.current = true;
    }, []);

    const dataKey = data.map((item) => item.id).join(",");

    useEffect(() => {
      const initialState = data.reduce((acc, item, rowIndex) => {
        acc[rowIndex] = {
          item_id: item.id,
          item_name: item.name,
          image_url: item.image_url,
          price: type === "in" ? item.cost_price : item.selling_price,
          item_current_quantity: item.stock,
          unit_conversion: item.unit_conversion,
          stock_quantity: "",
          transacted_at: new Date(),
          is_secondary: false,
          has_secondary: item.secondary_unit ? true : false,
          stock_units: {
            unit: item.unit,
            secondary_unit: item.secondary_unit,
            selected_unit: item.unit,
          },
        };
        return acc;
      }, {});
      setFieldStates(initialState);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey]);

    const handleFieldChange = (rowIndex, field, value) => {
      setFieldStates((prevState) => {
        const updatedRow = {
          ...prevState[rowIndex],
          [field]: value,
        };

        return {
          ...prevState,
          [rowIndex]: updatedRow,
        };
      });
      if (field === "stock_quantity") {
        if (value && value !== "") {
          const params = {
            isSecondary:
              paramsMap[rowIndex]?.isSecondary !== undefined
                ? paramsMap[rowIndex]?.isSecondary
                : fieldStates[rowIndex]?.is_secondary,
            amount: paramsMap[rowIndex]?.amount || fieldStates[rowIndex]?.price,
            stock: value,
            item_id: fieldStates[rowIndex]?.item_id,
            item_current_quantity: fieldStates[rowIndex]?.item_current_quantity,
            unit_conversion: fieldStates[rowIndex]?.unit_conversion,
            date:
              paramsMap[rowIndex]?.date || fieldStates[rowIndex]?.transacted_at,
            name: fieldStates[rowIndex]?.item_name,
            type,
          };
          setParamsMap((prevMap) => ({
            ...prevMap,
            [rowIndex]: params,
          }));
        } else {
          setParamsMap((prevMap) => {
            const updatedMap = { ...prevMap };
            delete updatedMap[rowIndex];
            return updatedMap;
          });
        }
      }

      if (field === "price") {
        if (fieldStates[rowIndex]?.stock_quantity) {
          const params = {
            isSecondary:
              paramsMap[rowIndex]?.isSecondary !== undefined
                ? paramsMap[rowIndex]?.isSecondary
                : fieldStates[rowIndex]?.is_secondary,
            amount: value,
            stock:
              paramsMap[rowIndex]?.stock ||
              fieldStates[rowIndex]?.stock_quantity,
            item_id: fieldStates[rowIndex]?.item_id,
            item_current_quantity: fieldStates[rowIndex]?.item_current_quantity,
            unit_conversion: fieldStates[rowIndex]?.unit_conversion,
            date:
              paramsMap[rowIndex]?.date || fieldStates[rowIndex]?.transacted_at,
            name: fieldStates[rowIndex]?.item_name,
            type,
          };

          setParamsMap((prevMap) => ({
            ...prevMap,
            [rowIndex]: params,
          }));
        }
      }

      if (field === "transacted_at") {
        const params = {
          isSecondary:
            paramsMap[rowIndex]?.isSecondary !== undefined
              ? paramsMap[rowIndex]?.isSecondary
              : fieldStates[rowIndex]?.is_secondary,
          amount: paramsMap[rowIndex]?.amount || fieldStates[rowIndex]?.price,
          stock:
            paramsMap[rowIndex]?.stock || fieldStates[rowIndex]?.stock_quantity,
          item_id: fieldStates[rowIndex]?.item_id,
          item_current_quantity: fieldStates[rowIndex]?.item_current_quantity,
          unit_conversion: fieldStates[rowIndex]?.unit_conversion,
          date: value,
          name: fieldStates[rowIndex]?.item_name,
          type,
        };
        setParamsMap((prevMap) => ({
          ...prevMap,
          [rowIndex]: params,
        }));
      }
    };

    const handleUnitToggle = (rowIndex) => {
      setFieldStates((prevState) => {
        const currentUnits = prevState[rowIndex]?.stock_units;
        if (!currentUnits || !currentUnits.secondary_unit) {
          return prevState;
        }

        let nextUnit;
        let isSecondary = false;

        if (currentUnits.selected_unit === currentUnits.unit) {
          nextUnit = currentUnits.secondary_unit;
          isSecondary = true;
        } else if (currentUnits.selected_unit === currentUnits.secondary_unit) {
          nextUnit = currentUnits.unit;
          isSecondary = false;
        }

        let price =
          type === "in"
            ? data[rowIndex].cost_price
            : data[rowIndex].selling_price || 0;
        let total = isSecondary
          ? price / (data[rowIndex]?.unit_conversion || 1)
          : price;
        total = total % 1 ? Number(total).toFixed(2) : total;
        const updatedRow = {
          ...prevState[rowIndex],
          price: total,
          is_secondary: isSecondary,
          stock_units: {
            ...currentUnits,
            selected_unit: nextUnit || "--",
          },
        };

        setParamsMap((prevMap) => {
          const updatedMap = { ...prevMap };
          if (updatedMap[rowIndex]) {
            updatedMap[rowIndex] = {
              ...updatedMap[rowIndex],
              selected_unit: nextUnit || "--",
              isSecondary: isSecondary,
              amount: total,
            };
          }
          return updatedMap;
        });

        return {
          ...prevState,
          [rowIndex]: updatedRow,
        };
      });
    };

    return (
      <Box className={styles.stockInOutContainer}>
        <Box
          className={`${styles.modalTableHeaderRow} stockInOutModal`}
          sx={{ gridTemplateColumns: STOCK_IN_OUT_GRID_TEMPLATE }}
        >
          <Box className={`${styles.modalTableHeaderColumn}`}>
            <ParagraphText className={"fontSize12 fontWeight500 newLightColor"}>
              {I18n.item_name}
            </ParagraphText>
          </Box>
          <Box className={`${styles.modalTableHeaderColumn}`}>
            <ParagraphText className={"fontSize12 fontWeight500 newLightColor"}>
              {I18n.current_qty}
            </ParagraphText>
          </Box>
          <Box className={`${styles.modalTableHeaderColumn}`}>
            <ParagraphText className={"fontSize12 fontWeight500 newLightColor"}>
              {type === "in" ? `${I18n.costs_rs}` : `${I18n.sell_rs}`}
            </ParagraphText>
          </Box>
          <Box className={`${styles.modalTableHeaderColumn}`}>
            <ParagraphText className={"fontSize12 fontWeight500 newLightColor"}>
              {type === "in" ? `${I18n.stock_in_qty}` : `${I18n.stock_out_qty}`}
            </ParagraphText>
          </Box>
          <Box className={`${styles.modalTableHeaderColumn}`}>
            <ParagraphText className={"fontSize12 fontWeight500 newLightColor"}>
              {I18n.date}
            </ParagraphText>
          </Box>
        </Box>
        <Box
          className={`generic-scrollbar DataRowsContainer ${styles.dataRowsContainer}`}
          id="StockInOutScrollableDiv"
        >
          <AppPagination
            data={{
              handleNext: () =>
                data?.length < totalStockCount ? handleGetMoreData() : () => { },
              pageLoad: isLoading,
              count: totalStockCount,
              list: data,
              scrollableTarget: "StockInOutScrollableDiv",
            }}
          >
            {data.length > 0 ? data.map((dataItem, rowIndex) => (
              <ItemRow
                key={rowIndex}
                className={className}
                dataItem={dataItem}
                fieldStates={fieldStates}
                rowIndex={rowIndex}
                handleFieldChange={handleFieldChange}
                handleUnitToggle={handleUnitToggle}
                isSingleStock={isSingleStock}
                gridTemplateColumns={STOCK_IN_OUT_GRID_TEMPLATE}
              />
            )) :
              totalAllStocks ? <Box className="marginTop20">
                <NonIdealNew
                subtitle={
                  I18n.no_result
                }
              /> </Box> : ""}
          </AppPagination>
        </Box>
      </Box>
    );
  },
);

export default StockInOutList;
